/**
 * PwaAdapter.js
 *
 * Platform adapter for standard browser / PWA environments.
 * SW registration is delegated entirely to the `usePwa` composable so all
 * update state lives in one reactive place and App.vue never has to touch
 * raw window events.
 */

import { usePwa } from '@/composables/usePwa.js';

export function createPwaAdapter() {

  async function initialize() {
    console.log('[Platform/PWA] Initialized');
    // Language and other platform-specific init goes here.
  }

  function getInitData() {
    return null;
  }

  function getTelegramUserId() {
    return null;
  }

  function openExternalLink(url) {
    window.open(url, '_blank');
  }

  /**
   * Open a t.me link. In a browser this is just an external link — the OS hands it
   * to the Telegram app if installed, otherwise Telegram's web page offers to open
   * it. The TMA adapter's implementation is the one that carries real behavior;
   * this exists so callers never branch on platform.
   *
   * @param {string} url Full https://t.me/... URL (see constants/support.js).
   */
  function openTelegramChat(url) {
    window.open(url, '_blank');
  }

  function hapticImpact(_style = 'medium') {
    if ('vibrate' in navigator) navigator.vibrate(10);
  }

  function hapticNotification(type = 'success') {
    if (!('vibrate' in navigator)) return;
    const patterns = {
      success: [10, 50, 10],
      error:   [50, 30, 50],
      warning: [30],
    };
    navigator.vibrate(patterns[type] ?? [10]);
  }

  function onBackButton(callback) {
    window.addEventListener('popstate', callback);
  }

  function offBackButton(callback) {
    window.removeEventListener('popstate', callback);
  }

  function showBackButton() { /* browser provides native back */ }
  function hideBackButton()  { /* no-op */ }

  /**
     * Register the service worker.
     *
     * Two paths:
     *   - PRODUCTION: delegates to usePwa().register() → useRegisterSW() from
     *     `virtual:pwa-register/vue` (vite-plugin-pwa's official Vue binding).
     *     The plugin's runtime exposes the update/offlineReady reactive refs
     *     so App.vue can drive the prompt UI.
     *   - DEV:TUNNEL: vite-plugin-pwa's `devOptions.enabled` is false (the
     *     plugin's dev SW pipeline serves a module worker that OneSignal v16
     *     can't classic-register against — see vite.config.js devOptions
     *     comment). Instead the `poolit:tunnel-prebuilt-sw` Vite middleware
     *     serves the pre-built classic SW (www/sw.js) at /sw.js*. With the
     *     plugin's virtual module inert here, we call register() directly
     *     so the registration actually happens. Update/offline UI is a
     *     no-op in dev:tunnel (not a QA requirement).
     *
     * Regular `npm run dev` keeps the original no-SW behavior — no
     * accumulating redundant workers from HMR reloads.
     */
  async function registerServiceWorker() {
    const isTunnel = import.meta.env.MODE === 'tunnel';

    if (import.meta.env.PROD) {
      const { register } = usePwa();
      await register();
      return;
    }

    if (isTunnel) {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        console.log('[PWA] dev:tunnel SW registered:', reg.scope);
      } catch (err) {
        console.error('[PWA] dev:tunnel SW registration failed:', err);
      }
      return;
    }

    console.log('[PWA] SW registration skipped in dev mode');
  }

  /**
   * Push-notifications init gate.
   *
   * This adapter method is the BOOLEAN TMA gate — it returns `true` when
   * the host environment supports push (PWA) and `false` in TMA. It does
   * NOT call `usePushNotifications()` directly: that composable relies on
   * `onScopeDispose` to tear down its listeners, which only fires inside
   * an active Vue effect scope. The composable must therefore be invoked
   * from `App.vue <script setup>` (deferred-work 2026-06-03 conformance
   * review item #4).
   *
   * App.vue inspects this boolean before invoking the composable:
   *   if (!isTma() && adapter.initPushNotifications()) { … }
   *
   * `false` in `npm run dev` (no SW → composable would no-op anyway).
   * `true` in `npm run dev:tunnel` so real-device QA can exercise the full
   * OneSignal init → permission → identity → subscribe chain over the
   * Cloudflare tunnel. `true` in production builds.
   */
  function initPushNotifications() {
    if (!import.meta.env.PROD && import.meta.env.MODE !== 'tunnel') return false;
    return true;
  }

  return {
    type: 'pwa',
    initialize,
    getInitData,
    getTelegramUserId,
    openExternalLink,
    openTelegramChat,
    hapticImpact,
    hapticNotification,
    onBackButton,
    offBackButton,
    showBackButton,
    hideBackButton,
    registerServiceWorker,
    initPushNotifications,
  };
}
